// @flow
import React, { Component, type Node, type ElementRef, Fragment } from 'react';
import { get } from 'lodash';
import Sticky from 'react-stickynode';

import { cleanAnchor } from 'Utils/anchor/anchorUtils';
import Responsive from 'Components/utils/responsive/responsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { Section } from 'Components/frameworks/section/Section.jsx';

import { DropDownNav } from 'Components/frameworks/drop-down-nav/DropDownNav.jsx';
import { Navbar } from 'Components/frameworks/navbar/Navbar.jsx';

type Props = {
  children: Array<Node>,
  navItems: Array<string>,
  pageType: string,
  getTrackingData?: string => TrackingData,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
};

type State = {
  selectedIndex: number,
  isSticky: boolean,
};

export class Sections extends Component<Props, State> {
  /* eslint-disable react/default-props-match-prop-types */
  static defaultProps = {
    children: [],
    navItems: [],
    pageType: '',
  };

  navBarRef: { current: ElementRef<'nav'> | null };
  navSelector: string;

  constructor(props: Props) {
    super(props);

    this.navBarRef = React.createRef();
    this.navSelector = props.pageType || 'Sections';

    this.state = {
      selectedIndex: 0,
      isSticky: false,
    };
  }

  onStateChange = (status: *) => {
    this.setState(() => ({ isSticky: status.status !== Sticky.STATUS_ORIGINAL }));
  };

  onSectionIntersectViewport = (event: *) => {
    const visiblePercentage = Math.floor(event.intersectionRatio * 100);
    const navBarHeight = this.navBarRef && get(this.navBarRef, 'current.offsetHeight');
    const selectedIndex = Number(event.target.dataset.index);

    if (
      event.isIntersecting &&
      visiblePercentage > 10 &&
      event.intersectionRect.top <= navBarHeight
    ) {
      this.setState(prevState => ({
        ...prevState,
        selectedIndex,
      }));
    }
  };

  getNavbarItems(): Array<*> {
    return this.props.navItems.map((label, index) => {
      const anchor = label ? cleanAnchor(label) : '';
      return {
        label,
        link: {
          'data-position': index,
          onClick: this.onNavItemClick(anchor),
          url: `#${anchor}`,
        },
      };
    });
  }

  onNavItemClick = (anchor: string) => {
    return (e: Event) => {
      e.preventDefault();
      // $FlowFixMe
      const selectedIndex = Number(e.target.dataset.position);
      const scrollRootOffsetSelector = `#${this.navSelector}`;

      scrollToElement(`#${anchor}`, 600, scrollRootOffsetSelector).then(() => {
        this.setState({ selectedIndex });
      });
    };
  };

  render() {
    const {
      children,
      navItems,
      getTrackingData,
      scrollBackwardLabel,
      scrollForwardLabel,
    } = this.props;

    const { isSticky, selectedIndex } = this.state;

    return (
      <Responsive.Consumer>
        {({ isTablet }) => (
          <Fragment>
            <Sticky className="relative z-3" onStateChange={this.onStateChange}>
              {isTablet ? (
                <Navbar
                  className={isSticky ? 'shadow-md' : ''}
                  getTrackingData={getTrackingData}
                  id={this.navSelector}
                  items={this.getNavbarItems()}
                  ref={this.navBarRef}
                  scrollBackwardLabel={scrollBackwardLabel}
                  scrollForwardLabel={scrollForwardLabel}
                  selectedItem={selectedIndex}
                />
              ) : (
                <DropDownNav
                  className={isSticky ? 'shadow-md slideInTop' : ''}
                  getTrackingData={getTrackingData}
                  id={this.navSelector}
                  items={this.getNavbarItems()}
                  ref={this.navBarRef}
                  selectedItem={selectedIndex}
                />
              )}
            </Sticky>
            {children.map((sectionLines, index) => (
              <Section
                index={index}
                key={navItems[index]}
                name={navItems[index]}
                onSectionIntersectViewport={this.onSectionIntersectViewport}
              >
                {sectionLines}
              </Section>
            ))}
          </Fragment>
        )}
      </Responsive.Consumer>
    );
  }
}
